/**
 * `<TimelineScopeToggle/>` — the per-task timeline's scope control.
 *
 * The founder's headline behaviour: a task's timeline shows everything below it
 * in the tree by default ("This task & sub-tasks"), with a one-click narrowing
 * to just the task itself ("This task only"). A small segmented control, calm
 * and factual — shared by the drawer timeline and the full-panel timeline.
 *
 * Copy is about the WORK ("this task", "sub-tasks") — never about a person (§A9).
 *
 * Editorial re-skin (#1043, Wave 2): theme-aware. EDITORIAL paints the pill on
 * warm paper with the active segment on `--surface` over a hairline rule and
 * `--teal` ink. LEGACY keeps the shipped soft-mint pill, byte-identical. The
 * segmented structure, copy, and every `data-testid` are shared.
 */

import type { HistoryScope } from "../api/useNodeHistory";
import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";

const OPTIONS: ReadonlyArray<{ scope: HistoryScope; label: string }> = [
  { scope: "subtree", label: "This task & sub-tasks" },
  { scope: "node", label: "This task only" },
];

export interface TimelineScopeToggleProps {
  readonly scope: HistoryScope;
  readonly onChange: (scope: HistoryScope) => void;
  /** Tighter sizing for the 400px drawer. */
  readonly compact?: boolean;
}

export function TimelineScopeToggle({
  scope,
  onChange,
  compact = false,
}: TimelineScopeToggleProps) {
  const theme = useMapTheme();
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  return (
    <div
      aria-label="Timeline scope"
      data-testid="timeline-scope-toggle"
      data-scope={scope}
      className="inline-flex items-center rounded-full p-0.5"
      style={{
        backgroundColor: editorial ? c.bgRecess : PALETTE.canvas,
        border: `1px solid ${editorial ? c.rule : PALETTE.softBorder}`,
      }}
    >
      {OPTIONS.map((opt) => {
        const on = opt.scope === scope;
        return (
          <button
            key={opt.scope}
            type="button"
            aria-pressed={on}
            data-testid={`timeline-scope-${opt.scope}`}
            onClick={() => onChange(opt.scope)}
            className="rounded-full font-semibold transition-colors"
            style={{
              padding: compact ? "3px 9px" : "4px 12px",
              fontSize: compact ? 11 : 12,
              backgroundColor: on ? (editorial ? c.cardBg : "#FFFFFF") : "transparent",
              color: on
                ? editorial
                  ? c.teal
                  : PALETTE.tealInk
                : editorial
                  ? c.muted
                  : PALETTE.muted,
              boxShadow: on ? "0 1px 2px rgba(12,42,42,0.10)" : "none",
              ...(editorial ? { fontFamily: theme.fonts.sans } : {}),
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
